import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Chip, Tooltip, Typography, Card, IconButton } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave, faUserMinus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

const AssignmentHistory = ({ assignments, onDeallocate, onDelete }) => {
    const getDuration = (start, end) => {
        const startDate = new Date(start);
        const endDate = end ? new Date(end) : new Date();
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const formatDuration = (days) => {
        if (days < 7) return `${days} days`;
        const weeks = Math.floor(days / 7);
        const remainingDays = days % 7;
        return `${weeks} wks ${remainingDays > 0 ? `${remainingDays} days` : ''}`;
    };

    return (
        <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="mb-6">
                <Typography variant="h5" color="blue-gray" className="font-bold">
                    Assignment History
                </Typography>
                <Typography variant="small" className="text-gray-500 font-normal">
                    View past and current driver assignments for this vehicle
                </Typography>
            </div>

            <div className="space-y-4">
                <div className="md:hidden space-y-3">
                    {assignments.length > 0 ? (
                        [...assignments].reverse().map((assignment) => (
                            <Card key={assignment.id} className="p-4 rounded-xl shadow-none border border-gray-100">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <Link to={`/drivers/${assignment.driver_id}`}>
                                            <Typography variant="small" color="blue" className="font-bold hover:underline">
                                                {assignment.driver_id}
                                            </Typography>
                                        </Link>
                                        <Typography variant="small" color="blue-gray" className="font-bold">
                                            {assignment.driver_name}
                                        </Typography>
                                    </div>
                                    {assignment.end_date === null ? (
                                        <Chip size="sm" variant="ghost" color="green" value="ACTIVE" className="rounded-sm px-4 font-bold" />
                                    ) : (
                                        <Chip size="sm" variant="ghost" color="red" value="INACTIVE" className="rounded-sm px-4 font-bold" />
                                    )}
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-xs text-gray-500">Assignment Period</div>
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {format(new Date(assignment.start_date), 'MMM dd, yyyy')} -{' '}
                                            {assignment.end_date ? format(new Date(assignment.end_date), 'MMM dd, yyyy') : 'To Date'}
                                        </Typography>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Duration</div>
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {formatDuration(getDuration(assignment.start_date, assignment.end_date))}
                                        </Typography>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Weekly Rate</div>
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'KES',
                                                minimumFractionDigits: 2,
                                            }).format(Number(assignment.weekly_amount))}
                                        </Typography>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Driver Balance</div>
                                        <Typography
                                            variant="small"
                                            color={Number(assignment.driver_balance) < 0 ? "red" : "green"}
                                            className="font-bold"
                                        >
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'KES',
                                                minimumFractionDigits: 2,
                                            }).format(Number(assignment.driver_balance || 0))}
                                        </Typography>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    {assignment.end_date === null && (
                                        <IconButton
                                            variant="outlined"
                                            color="red"
                                            size="sm"
                                            className="rounded-full border-red-200 hover:bg-red-50"
                                            onClick={() => {
                                                const deallocateDate = prompt(
                                                    'Enter deallocation date (YYYY-MM-DD):',
                                                    new Date().toISOString().split('T')[0]
                                                );
                                                if (deallocateDate) {
                                                    onDeallocate(assignment.allocation_number, deallocateDate);
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faUserMinus} />
                                        </IconButton>
                                    )}
                                    <a href={`/drivers/${assignment.driver_id}/payments`}>
                                        <IconButton variant="outlined" color="orange" size="sm" className="rounded-full border-orange-200 hover:bg-orange-50">
                                            <FontAwesomeIcon icon={faMoneyBillWave} />
                                        </IconButton>
                                    </a>
                                    <IconButton
                                        variant="outlined"
                                        color="red"
                                        size="sm"
                                        className="rounded-full border-red-200 hover:bg-red-50"
                                        onClick={() => onDelete(assignment.allocation_number)}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </IconButton>
                                </div>
                            </Card>
                        ))
                    ) : (
                        <Card className="p-4 rounded-xl shadow-none border border-gray-100 text-center text-gray-500">
                            No drivers assigned yet.
                        </Card>
                    )}
                </div>

                <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-max table-auto text-left">
                    <thead>
                        <tr>
                            {['Driver ID', 'Driver Name', 'Assignment Period', 'Duration', 'Weekly Rate', 'Driver Balance', 'Status', 'Actions'].map((head) => (
                                <th key={head} className={`border-b border-blue-gray-100 bg-blue-gray-50/50 p-4 ${head === 'Driver Balance' ? 'text-right' : ''}`}>
                                    <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-bold leading-none opacity-70"
                                    >
                                        {head}
                                    </Typography>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {assignments.length > 0 ? (
                            [...assignments].reverse().map((assignment) => (
                                <tr key={assignment.id} className="even:bg-blue-gray-50/50 hover:bg-blue-gray-50 transition-colors">
                                    <td className="p-4">
                                        <Link to={`/drivers/${assignment.driver_id}`}>
                                            <Typography variant="small" color="blue" className="font-bold hover:underline">
                                                {assignment.driver_id}
                                            </Typography>
                                        </Link>
                                    </td>
                                    <td className="p-4">
                                        <Typography variant="small" color="blue-gray" className="font-bold">
                                            {assignment.driver_name}
                                        </Typography>
                                    </td>
                                    <td className="p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {format(new Date(assignment.start_date), 'MMM dd, yyyy')} -{' '}
                                            {assignment.end_date
                                                ? format(new Date(assignment.end_date), 'MMM dd, yyyy')
                                                : 'To Date'}
                                        </Typography>
                                    </td>
                                    <td className="p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {formatDuration(getDuration(assignment.start_date, assignment.end_date))}
                                        </Typography>
                                    </td>
                                    <td className="p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'KES',
                                                minimumFractionDigits: 2,
                                            }).format(Number(assignment.weekly_amount))}
                                        </Typography>
                                    </td>
                                    <td className="p-4 text-right">
                                        <Typography 
                                            variant="small" 
                                            color={Number(assignment.driver_balance) < 0 ? "red" : "green"} 
                                            className="font-bold"
                                        >
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'KES',
                                                minimumFractionDigits: 2,
                                            }).format(Number(assignment.driver_balance || 0))}
                                        </Typography>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex justify-center">
                                            {assignment.end_date === null ? (
                                                <Chip size="sm" variant="ghost" color="green" value="ACTIVE" className="rounded-sm px-4 font-bold" />
                                            ) : (
                                                <Chip size="sm" variant="ghost" color="red" value="INACTIVE" className="rounded-sm px-4 font-bold" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center justify-center gap-2">
                                            {assignment.end_date === null && (
                                                <Tooltip content="Deallocate Driver">
                                                    <IconButton
                                                        variant="outlined"
                                                        color="red"
                                                        size="sm"
                                                        className="rounded-full border-red-200 hover:bg-red-50"
                                                        onClick={() => {
                                                            const deallocateDate = prompt(
                                                                'Enter deallocation date (YYYY-MM-DD):',
                                                                new Date().toISOString().split('T')[0]
                                                            );
                                                            if (deallocateDate) {
                                                                onDeallocate(
                                                                    assignment.allocation_number,
                                                                    deallocateDate
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faUserMinus} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip content="View Payments">
                                                <a href={`/drivers/${assignment.driver_id}/payments`}>
                                                    <IconButton variant="outlined" color="orange" size="sm" className="rounded-full border-orange-200 hover:bg-orange-50">
                                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                                    </IconButton>
                                                </a>
                                            </Tooltip>
                                            <Tooltip content="Delete Assignment">
                                                <IconButton
                                                    variant="outlined"
                                                    color="red"
                                                    size="sm"
                                                    className="rounded-full border-red-200 hover:bg-red-50"
                                                    onClick={() =>
                                                        onDelete(assignment.allocation_number)
                                                    }
                                                >
                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="p-4 text-center text-gray-500">
                                    No drivers assigned yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {assignments.length > 0 && (
                        <tfoot>
                            <tr className="bg-blue-gray-50/50">
                                <td colSpan="3" className="p-4 text-right">
                                    <Typography variant="small" color="blue-gray" className="font-bold">
                                        Total Duration:
                                    </Typography>
                                </td>
                                <td className="p-4">
                                    <Typography variant="small" color="blue-gray" className="font-bold">
                                        {formatDuration(assignments.reduce((acc, curr) => acc + getDuration(curr.start_date, curr.end_date), 0))}
                                    </Typography>
                                </td>
                                <td colSpan="4"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
                </div>
            </div>
        </Card>
    );
};

export default AssignmentHistory;
